import type {ResourcesSection} from './types';

/** Fixed pages a landing resource card can point at. */
export type SitePage = 'resources' | 'notices';

export const SITE_PAGES: readonly {page: SitePage; label: string}[] = [
  {page: 'resources', label: 'Plan resources page'},
  {page: 'notices', label: 'Annual notices page'},
];

// Stored as a slug-prefixed path, the same shape existing configs already use.
export function sitePageHref(slug: string, page: SitePage): string {
  return `/${slug}/${page}`;
}

export function sitePageOf(href: string, slug: string): SitePage | undefined {
  return SITE_PAGES.find((p) => href === sitePageHref(slug, p.page))?.page;
}

/** A `/{other-slug}/{page}` link: points at another site's resources or notices page. */
export function isOtherSitePageHref(href: string, slug: string): boolean {
  const match = /^\/([a-z0-9-]+)\/(resources|notices)$/.exec(href);
  return !!match && match[1] !== slug;
}

const SAFE_SCHEME = /^(https?|mailto|tel):/i;
const ANY_SCHEME = /^[a-z][a-z0-9+.-]*:/i;

/** Adds https:// to bare addresses (`www.google.com`); drops unsafe schemes like `javascript:`. */
export function absoluteHref(href: string): string {
  const v = href.trim();
  if (!v || v.startsWith('/') || v.startsWith('#') || SAFE_SCHEME.test(v)) return v;
  if (ANY_SCHEME.test(v)) return '';
  return `https://${v}`;
}

export function isPdfHref(href: string): boolean {
  return /\.pdf($|[?#])/i.test(href);
}

/** Point this site's page links at `toSlug` (mirroring from another site, or a slug rename). */
export function retargetSiteLinks(
  section: ResourcesSection | undefined,
  fromSlug: string,
  toSlug: string,
): ResourcesSection | undefined {
  if (!section || fromSlug === toSlug) return section;
  const prefix = `/${fromSlug}/`;
  return {
    ...section,
    items: section.items.map((item) =>
      item.href.startsWith(prefix)
        ? {...item, href: `/${toSlug}/${item.href.slice(prefix.length)}`}
        : item,
    ),
  };
}
